import React, { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { PerfilPermissao, PermissaoPerfil, ModuloSistema, AcaoSistema, Usuario } from '../../../../types/admin';
import { API_BASE_URL } from '../../../../environment';

interface ModalDetalhesPerfilProps {
  perfil: PerfilPermissao;
  onClose: () => void;
  onRefresh: () => void;
}

export const ModalDetalhesPerfil: React.FC<ModalDetalhesPerfilProps> = ({
  perfil,
  onClose,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState<'permissoes' | 'usuarios' | 'atividade'>('permissoes');
  const [permissoes, setPermissoes] = useState<PermissaoPerfil[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  // URL base da API (removido /api/v1 para usar apenas o domínio base)
  const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');

  // Buscar permissões do perfil
  const fetchPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await apiService.getPermissoesPerfil({ id_perfil: perfil.id });
      setPermissoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar permissões:', error);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  // Buscar usuários do perfil
  const fetchUsuarios = async () => {
    try {
      setLoadingUsuarios(true);
      const response = await apiService.getUsuarios();
      // Filtrar apenas usuários deste perfil
      const usuariosDoPerfil = response.data.filter(usuario => 
        usuario.id_perfil_base === perfil.id
      );
      setUsuarios(usuariosDoPerfil);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Carregar dados quando a aba muda
  useEffect(() => {
    if (activeTab === 'permissoes') {
      fetchPermissoes();
    } else if (activeTab === 'usuarios') {
      fetchUsuarios();
    }
  }, [activeTab, perfil.id]);

  // Agrupar permissões por módulo
  const permissoesPorModulo = permissoes.reduce((acc, permissao) => {
    const moduloNome = permissao.modulo_nome || 'Módulo Desconhecido';
    if (!acc[moduloNome]) {
      acc[moduloNome] = [];
    }
    acc[moduloNome].push(permissao);
    return acc;
  }, {} as Record<string, PermissaoPerfil[]>);

  return (
    <div 
      className="modal fade show" 
      id="modal_detalhes_perfil" 
      style={{ display: 'block' }}
      aria-modal="true"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-light">
            <div className="d-flex align-items-center">
              <div 
                className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3"
                style={{ 
                  backgroundColor: perfil.cor || '#6B7280',
                  width: '48px',
                  height: '48px'
                }}
              >
                {perfil.nome.charAt(0).toUpperCase()}
              </div>
              <div>
                <h5 className="modal-title mb-0 d-flex align-items-center">
                  {perfil.nome}
                  {perfil.is_padrao && (
                    <span className="badge bg-info ms-2">
                      <i className="ti ti-shield-check me-1"></i>
                      Perfil Padrão
                    </span>
                  )}
                </h5>
                <small className="text-muted">{perfil.codigo}</small>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-0">
            {/* Nav Tabs */}
            <div className="border-bottom">
              <nav className="nav nav-tabs nav-line-tabs nav-line-tabs-2x">
                <button
                  className={`nav-link ${activeTab === 'permissoes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('permissoes')}
                >
                  <i className="ti ti-shield-check me-2"></i>
                  Permissões
                </button>
                <button
                  className={`nav-link ${activeTab === 'usuarios' ? 'active' : ''}`}
                  onClick={() => setActiveTab('usuarios')}
                >
                  <i className="ti ti-users me-2"></i>
                  Usuários ({perfil.total_usuarios || 0})
                </button>
                <button
                  className={`nav-link ${activeTab === 'atividade' ? 'active' : ''}`}
                  onClick={() => setActiveTab('atividade')}
                >
                  <i className="ti ti-clock-hour-4 me-2"></i>
                  Atividade
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {/* Aba Permissões */}
              {activeTab === 'permissoes' && (
                <div className="tab-pane active">
                  {loadingPermissoes ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Informações do Perfil */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title">Informações Gerais</h6>
                              <div className="row">
                                <div className="col-6">
                                  <small className="text-muted d-block">Status:</small>
                                  {perfil.ativo ? (
                                    <span className="badge bg-success">✅ Ativo</span>
                                  ) : (
                                    <span className="badge bg-danger">❌ Inativo</span>
                                  )}
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Tipo:</small>
                                  {perfil.is_padrao ? (
                                    <span className="badge bg-info">🛡️ Padrão</span>
                                  ) : (
                                    <span className="badge bg-primary">👤 Personalizado</span>
                                  )}
                                </div>
                              </div>
                              {perfil.descricao && (
                                <div className="mt-2">
                                  <small className="text-muted d-block">Descrição:</small>
                                  <p className="mb-0">{perfil.descricao}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title">Estatísticas</h6>
                              <div className="row">
                                <div className="col-6">
                                  <small className="text-muted d-block">Usuários:</small>
                                  <span className="fw-bold">{perfil.total_usuarios || 0}</span>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">Criado em:</small>
                                  <span>{new Date(perfil.data_criacao).toLocaleDateString('pt-BR')}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Permissões por Módulo */}
                      <h6 className="mb-3">Permissões por Módulo</h6>
                      {Object.keys(permissoesPorModulo).length === 0 ? (
                        <div className="text-center py-4">
                          <i className="ti ti-shield-off fs-2 text-muted"></i>
                          <p className="text-muted mt-2">Nenhuma permissão configurada</p>
                        </div>
                      ) : (
                        <div className="accordion" id="permissoesAccordion">
                          {Object.entries(permissoesPorModulo).map(([moduloNome, permissoesModulo], index) => (
                            <div key={moduloNome} className="accordion-item">
                              <h2 className="accordion-header">
                                <button
                                  className="accordion-button collapsed"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#collapse${index}`}
                                  aria-expanded="false"
                                >
                                  <i className="ti ti-box me-2"></i>
                                  <strong>{moduloNome}</strong>
                                  <span className="badge bg-primary ms-2">{permissoesModulo.length} ações</span>
                                </button>
                              </h2>
                              <div
                                id={`collapse${index}`}
                                className="accordion-collapse collapse"
                                data-bs-parent="#permissoesAccordion"
                              >
                                <div className="accordion-body">
                                  <div className="row">
                                    {permissoesModulo.map((permissao) => (
                                      <div key={permissao.id} className="col-md-6 mb-2">
                                        <div className="d-flex align-items-center">
                                          {permissao.permitido ? (
                                            <i className="ti ti-check-circle text-success me-2"></i>
                                          ) : (
                                            <i className="ti ti-x-circle text-danger me-2"></i>
                                          )}
                                          <span>{permissao.acao_nome}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Aba Usuários */}
              {activeTab === 'usuarios' && (
                <div className="tab-pane active">
                  {loadingUsuarios ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h6 className="mb-3">Usuários com este Perfil</h6>
                      {usuarios.length === 0 ? (
                        <div className="text-center py-4">
                          <i className="ti ti-user-off fs-2 text-muted"></i>
                          <p className="text-muted mt-2">Nenhum usuário associado a este perfil</p>
                          <small className="text-muted">Este perfil está órfão</small>
                        </div>
                      ) : (
                        <div className="row">
                          {usuarios.map((usuario) => (
                            <div key={usuario.id} className="col-md-6 col-lg-4 mb-3">
                              <div className="card h-100">
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <div className="avatar avatar-sm me-2">
                                      {usuario.foto_perfil_url ? (
                                        <img 
                                          src={BASE_DOMAIN + usuario.foto_perfil_url} 
                                          alt={usuario.nome}
                                          className="avatar-img rounded-circle"
                                        />
                                      ) : (
                                        <div className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                                          {usuario.nome.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-0">{usuario.nome} {usuario.sobrenome || ''}</h6>
                                      <small className="text-muted">{usuario.email}</small>
                                      {usuario.cargo && (
                                        <small className="d-block text-muted">{usuario.cargo}</small>
                                      )}
                                    </div>
                                    <div>
                                      {usuario.ativo ? (
                                        <span className="badge bg-success-light text-success">Ativo</span>
                                      ) : (
                                        <span className="badge bg-danger-light text-danger">Inativo</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Aba Atividade */}
              {activeTab === 'atividade' && (
                <div className="tab-pane active">
                  <h6 className="mb-3">Log de Atividades</h6>
                  <div className="text-center py-4">
                    <i className="ti ti-clock-hour-4 fs-2 text-muted"></i>
                    <p className="text-muted mt-2">Log de atividades ainda não implementado</p>
                    <small className="text-muted">Esta funcionalidade será adicionada em breve</small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Fechar
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={() => {
                // Implementar abertura do modal de edição
                onClose();
              }}
            >
              <i className="ti ti-edit me-1"></i>
              Editar Perfil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};